import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@lendcord/concord";

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TermsSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

// Legal text — kept as plain English here rather than run through react-intl
// like the rest of this app's copy. It's sourced verbatim from the Terms &
// Conditions draft and needs to read as one exact, reviewable document, not
// be assembled from independently-translatable fragments.
const TERMS_SECTIONS: TermsSection[] = [
  {
    heading: "1. Introduction",
    paragraphs: [
      'Welcome to Lendcord ("Lendcord" or the "Platform"), a digital lending platform owned and operated by Capicord Technology Private Limited ("Company", "we", "our", or "us").',
      "These Terms & Conditions (\"Terms\") govern your access to and use of the Lendcord website, mobile application, and digital lending platform (\"Platform\").",
      "By creating an account, uploading documents, or applying for any financial product through the Platform, you acknowledge that you have read, understood, and agreed to these Terms, our Privacy Policy, and any additional consents requested during the application process.",
    ],
  },
  {
    heading: "2. About Lendcord",
    paragraphs: [
      "Lendcord is the brand name of Capicord Technology Private Limited, a technology-enabled financial services platform.",
      "Lendcord facilitates loan applications by connecting eligible customers with RBI-regulated Banks, NBFCs, P2P NBFCs, Small Finance Banks, Housing Finance Companies, and other regulated financial institutions.",
      "Capicord Technology Private Limited acts as a technology platform and Loan Service Provider (LSP). Unless specifically stated otherwise, the Company does not lend its own funds, sanction loans, or guarantee loan approval. Loan approval, loan amount, interest rate, tenure, charges, repayment schedule, and disbursement remain solely at the discretion of the respective lending institution.",
    ],
  },
  {
    heading: "3. Eligibility",
    paragraphs: ["To use the Platform, you must:"],
    list: [
      "Be at least 18 years of age.",
      "Be legally competent to enter into contracts.",
      "Be an Indian resident.",
      "Provide genuine, accurate, and complete information.",
      "Comply with all applicable laws and regulations.",
    ],
  },
  {
    heading: "4. Customer Declaration",
    paragraphs: ["You confirm that:"],
    list: [
      "All documents uploaded by you are genuine and legally valid.",
      "Information provided by you is accurate, complete, and up to date.",
      "You are the lawful owner or authorised holder of the submitted documents.",
      "You shall immediately inform Capicord Technology Private Limited if any information provided by you changes.",
    ],
  },
  {
    heading: "",
    paragraphs: [
      "Providing false, misleading, forged, or fraudulent information may result in rejection of your application, suspension of your account, reporting to regulatory authorities, and legal action.",
    ],
  },
  {
    heading: "5. Information We Collect",
    paragraphs: ["You authorise Capicord Technology Private Limited, through the Lendcord Platform, to collect and process information including:"],
    list: [
      "Full Name",
      "Date of Birth",
      "Mobile Number",
      "Email Address",
      "Residential Address",
      "Aadhaar Number (where legally permitted)",
      "PAN Number",
      "Bank Account Details",
      "Bank Statements",
      "GST Details",
      "Salary Slips",
      "Income Tax Returns",
      "Business Financials",
      "Employment Information",
      "Business Documents",
      "Device Information",
      "IP Address",
      "Browser Information",
      "Location Information (where required)",
      "Login Activity",
      "Credit Bureau Information",
      "Video KYC Data (where applicable)",
    ],
  },
  {
    heading: "6. Purpose of Data Collection",
    paragraphs: ["Your information may be used for:"],
    list: [
      "Customer identification.",
      "KYC verification.",
      "Credit underwriting.",
      "Fraud prevention.",
      "Loan eligibility assessment.",
      "Risk management.",
      "Regulatory compliance.",
      "Customer support.",
      "Loan servicing.",
      "Collection activities.",
      "Internal analytics.",
      "Improving the Platform and user experience.",
    ],
  },
  {
    heading: "7. Consent for Verification",
    paragraphs: ["You expressly authorise Capicord Technology Private Limited and its lending partners to verify your information through legally permitted sources, including:"],
    list: [
      "PAN Verification",
      "Aadhaar Verification (where legally permitted)",
      "CKYC",
      "Credit Information Companies (CIBIL, Experian, Equifax, CRIF High Mark)",
      "Bank Statement Verification",
      "GST Verification",
      "Income Verification",
      "Employment Verification",
      "Business Verification",
      "Account Aggregator Framework (with your separate consent)",
    ],
  },
  {
    heading: "8. Consent to Share Information",
    paragraphs: ["You authorise Capicord Technology Private Limited to share your information with:"],
    list: [
      "RBI-regulated Banks",
      "NBFCs",
      "P2P NBFCs",
      "Small Finance Banks",
      "Housing Finance Companies",
      "Credit Bureaus",
      "Account Aggregators",
      "KYC Service Providers",
      "Technology Service Providers",
      "Collection Agencies",
      "Legal Authorities",
      "Government Agencies",
      "Regulatory Authorities",
    ],
  },
  {
    heading: "",
    paragraphs: [
      "Such sharing shall only be for loan processing, credit assessment, compliance, servicing, fraud prevention, recovery, customer support, or as otherwise required by applicable law.",
    ],
  },
  {
    heading: "9. Communications",
    paragraphs: ["You consent to receive communications from Lendcord, operated by Capicord Technology Private Limited, through:"],
    list: ["Phone Calls", "SMS", "WhatsApp", "Email", "Push Notifications", "In-App Notifications"],
  },
  {
    heading: "",
    paragraphs: ["These communications may relate to:"],
    list: [
      "Loan Applications",
      "KYC",
      "Verification",
      "Disbursement",
      "EMI Reminders",
      "Collections",
      "Service Updates",
      "Promotional Offers",
      "Regulatory Notifications",
    ],
  },
  {
    heading: "10. Customer Responsibilities",
    paragraphs: ["You agree to:"],
    list: [
      "Maintain the confidentiality of your login credentials.",
      "Not impersonate another individual or entity.",
      "Use the Platform only for lawful purposes.",
      "Cooperate during verification and loan processing.",
      "Repay loans according to the terms agreed with the respective lender.",
    ],
  },
  {
    heading: "11. Loan Approval",
    paragraphs: [
      "Submitting a loan application through Lendcord does not guarantee loan approval.",
      "Loan approval depends entirely upon the respective lending institution's:",
    ],
    list: ["Credit Policy", "Internal Risk Assessment", "Regulatory Requirements", "KYC Compliance", "Document Verification"],
  },
  {
    heading: "",
    paragraphs: [
      "Capicord Technology Private Limited has no authority to approve or reject loans on behalf of lenders unless expressly authorised under a written agreement.",
    ],
  },
  {
    heading: "12. Fees",
    paragraphs: [
      "Unless specifically disclosed, Capicord Technology Private Limited does not charge customers merely for submitting a loan application through Lendcord.",
      "Any applicable processing fees, convenience charges, documentation charges, or lender fees shall be clearly disclosed before acceptance.",
    ],
  },
  {
    heading: "13. Intellectual Property",
    paragraphs: [
      "All intellectual property rights relating to the Lendcord Platform, including its name, logo, software, website, mobile application, trademarks, graphics, content, and technology, are the exclusive property of Capicord Technology Private Limited.",
      "No content may be copied, reproduced, modified, distributed, or commercially exploited without prior written permission from the Company.",
    ],
  },
  {
    heading: "14. Fraud & Misuse",
    paragraphs: ["Capicord Technology Private Limited reserves the right to suspend or terminate any account involved in:"],
    list: [
      "Identity Theft",
      "Fake Documents",
      "Money Laundering",
      "Misrepresentation",
      "Cyber Fraud",
      "Unauthorised Access",
      "Abuse of the Platform",
    ],
  },
  {
    heading: "",
    paragraphs: ["The Company may report such activities to the appropriate regulatory, law enforcement, or government authorities."],
  },
  {
    heading: "15. Limitation of Liability",
    paragraphs: ["Capicord Technology Private Limited shall not be liable for:"],
    list: [
      "Loan rejection.",
      "Delayed disbursement by lenders.",
      "Technical interruptions.",
      "Incorrect information provided by users.",
      "Decisions made by lending partners.",
      "Third-party system failures.",
      "Any indirect, incidental, or consequential loss arising from the use of the Platform.",
    ],
  },
  {
    heading: "16. Privacy",
    paragraphs: [
      "Your personal information shall be collected, processed, stored, and shared in accordance with the Lendcord Privacy Policy and applicable Indian laws, including the Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000, RBI Digital Lending Guidelines, and other applicable regulations.",
    ],
  },
  {
    heading: "17. Changes to Terms",
    paragraphs: [
      "Capicord Technology Private Limited reserves the right to amend or revise these Terms & Conditions at any time.",
      "Continued use of the Lendcord Platform after publication of revised Terms constitutes your acceptance of the updated Terms.",
    ],
  },
  {
    heading: "18. Governing Law",
    paragraphs: [
      "These Terms shall be governed by and construed in accordance with the laws of India.",
      "The courts having jurisdiction over the registered office of Capicord Technology Private Limited shall have exclusive jurisdiction to hear and decide any disputes arising out of or relating to these Terms.",
    ],
  },
  {
    heading: "19. Grievance Redressal",
    paragraphs: [
      "For any complaint, query, or grievance relating to the Lendcord Platform, please contact:",
      "Grievance Officer",
      "Capicord Technology Private Limited",
      "Email: support@capicord.com",
      "Phone: +91",
      "Website: www.lendcord.in",
      "Working Hours: Monday to Friday, 10:00 AM to 6:00 PM (IST)",
    ],
  },
  {
    heading: "20. Acceptance",
    paragraphs: ['By clicking "I Agree", creating an account, uploading documents, or applying for any financial product through Lendcord, you confirm that:'],
    list: [
      "You have read and understood these Terms & Conditions.",
      "You agree to be legally bound by these Terms.",
      "You consent to the collection, processing, verification, storage, and sharing of your information by Capicord Technology Private Limited as described in these Terms and the Privacy Policy.",
      "You understand that loan approval and disbursement remain solely at the discretion of the respective lending institution.",
    ],
  },
];

export function TermsDialog({ open, onOpenChange }: TermsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl flex-col p-0">
        <DialogHeader className="border-b border-border px-6 pb-4 pt-6">
          <DialogTitle>Terms & Conditions</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-5 text-sm text-text">
            {TERMS_SECTIONS.map((section, index) => (
              <div key={index} className="flex flex-col gap-2">
                {section.heading && <h3 className="text-base font-bold text-text">{section.heading}</h3>}
                {section.paragraphs?.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex} className="leading-relaxed text-text-muted">
                    {paragraph}
                  </p>
                ))}
                {section.list && (
                  <ul className="list-disc space-y-1 pl-5 text-text-muted">
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
